import React, { useState, useEffect } from "react";
import { Grid, Box, Button, Typography, CircularProgress, TextField } from "@mui/material";
import { Formik, Form, FastField } from "formik";
import * as Yup from "yup";
import { useCompany } from "../../contexts/CompanyContext";
import { createPhysicalInventoryCount, getPhysicalInventoryCountById, updatePhysicalInventoryCount } from "../../api/physicalInventoryCounts/index.ts";
import { useSnack } from "../../services/SnakbarProvider.js";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import { getUserWarehousesByUserId } from "../../api/userWarehouses/index.js";
import { useUser } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

interface PhysicalInventoryCountFormProps {
  onSubmit: () => void;
  id?: number;
}

const validationSchema = Yup.object().shape({
  companyId: Yup.number().required("La empresa es requerida"),
  warehouseId: Yup.number().required("El almacén es requerido"),
  code: Yup.string().required("El código es requerido"),
});

const PhysicalInventoryCountForm: React.FC<PhysicalInventoryCountFormProps> = ({ onSubmit, id }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<any>({
    companyId: null,
    warehouseId: null,
    code: "",
  });
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const { selectedCompany } = useCompany();
  const { userData } = useUser();
  const { openSnack } = useSnack();
  const navigate = useNavigate();

  const fetchWarehouses = async () => {
    if (!userData?.id) return;
    try {
      const response = await getUserWarehousesByUserId(userData.id);
      setWarehouses(response.data || []);
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getPhysicalInventoryCountById(id);
      const data = response.data;
      setFormData({
        companyId: data.companyId,
        warehouseId: data.warehouseId,
        code: data.code || "",
      });
    } catch (error) {
      console.log(error);
      openSnack("Error al cargar el conteo físico", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
    if (formMode === "edit" && id) {
      fetchData();
    } else {
      setFormData({
        companyId: selectedCompany.id,
        warehouseId: null,
        code: "",
      });
      setLoading(false);
    }
  }, [id, selectedCompany.id, formMode, userData?.id]);

  const generateCode = (warehouseCode: string) => {
    const year = dayjs().format("YYYY");
    return `${warehouseCode}-${year}`;
  };

  const createPayload = (values: any) => {
    return {
      ...values,
      companyId: selectedCompany.id,
    };
  };

  const handleSubmit = async (values: any) => {
    const payload = createPayload(values);
    try {
      if (id) {
        await updatePhysicalInventoryCount(id, payload);
        openSnack("Conteo físico actualizado correctamente", "success");
        onSubmit();
      } else {
        const response = await createPhysicalInventoryCount(payload);
        openSnack("Conteo físico creado correctamente", "success");
        // Navigate to dashboard after creation
        if (response.data?.id) {
          navigate(`/physical-inventory-counts/dashboard/${response.data.id}`, {
            state: response.data
          });
        } else {
          onSubmit();
        }
      }
    } catch (error) {
      openSnack("Error al guardar el conteo físico", "error");
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Formik
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, errors }) => (
        <Form>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Conteo Físico de Inventario
            </Typography>
            <Grid container spacing={2} sx={{ alignItems: "center" }}> 
              <Grid item xs={12} md={6}>
                <FastField
                  name="warehouseId"
                  as={DropdownSelector}
                  label="Almacén"
                  fetchFunction={getUserWarehousesByUserId}
                  fetchParams={userData?.id}
                  optionLabel={"warehouse.name"}
                  keyField={"warehouseId"}
                  value={values.warehouseId}
                  onChange={(event: any, option: any) => {
                    setFieldValue("warehouseId", event.target.value);
                    if (option && !id) {
                      // Generate code only when creating (not editing)
                      // Try code first, then symbol, then name as fallback
                      const warehouseCode = option.warehouse?.code || option.warehouse?.symbol || option.warehouse?.name || "";
                      if (warehouseCode) {
                        const generatedCode = generateCode(warehouseCode);
                        setFieldValue("code", generatedCode);
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FastField
                  name="code"
                  as={TextField}
                  label="Código"
                  variant="outlined"
                  fullWidth
                  value={values.code}
                  error={!!errors.code}
                  helperText={errors.code}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    {formMode === "edit" ? "Actualizar" : "Guardar"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default PhysicalInventoryCountForm;
